import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { ASSETS } from "@/data";
import { EVAL_CRITERIA } from "@/lib/memberAuth";

const NAVY = [10, 26, 74];
const toBase64 = (buf) => {
  let s = "";
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  return btoa(s);
};
const fetchB64 = (url) => fetch(url).then((r) => r.arrayBuffer()).then(toBase64);
const logoDataUrl = (url, px = 420) => new Promise((resolve, reject) => {
  const img = new Image(); img.crossOrigin = "anonymous";
  img.onload = () => { const c = document.createElement("canvas"); c.width = c.height = px; const x = c.getContext("2d"); x.fillStyle = "#fff"; x.fillRect(0, 0, px, px); x.drawImage(img, 0, 0, px, px); resolve(c.toDataURL("image/jpeg", 0.9)); };
  img.onerror = reject; img.src = url;
});

async function loadFonts(doc) {
  const [reg, bold] = await Promise.all(["LiberationSans-Regular.ttf", "LiberationSans-Bold.ttf"].map((f) => fetchB64(`/fonts/${f}`)));
  doc.addFileToVFS("Lib-Regular.ttf", reg); doc.addFont("Lib-Regular.ttf", "Lib", "normal");
  doc.addFileToVFS("Lib-Bold.ttf", bold); doc.addFont("Lib-Bold.ttf", "Lib", "bold");
}

const fmtD = (iso) => new Date(iso).toLocaleDateString("el-GR", { day: "numeric", month: "long", year: "numeric" });
const verdict = (v) => (v >= 18 ? "Άριστα" : v >= 15 ? "Πολύ καλά" : v >= 12 ? "Καλά" : v >= 10 ? "Μέτρια" : "Χρειάζεται προσπάθεια");
const color = (v) => (v >= 15 ? [4, 120, 87] : v >= 10 ? [217, 119, 6] : [185, 28, 28]);

export async function exportEvaluationPdf({ ev, student }) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  await loadFonts(doc);
  const W = 210;

  doc.setFillColor(...NAVY); doc.rect(0, 0, W, 42, "F");
  doc.setFillColor(255); doc.roundedRect(12, 5, 32, 32, 2, 2, "F");
  try { doc.addImage(await logoDataUrl(ASSETS.logo), "JPEG", 13, 6, 30, 30); } catch (e) { /* logo optional */ }
  doc.setTextColor(255); doc.setFont("Lib", "bold"); doc.setFontSize(20); doc.text("Αξιολόγηση Προόδου", 50, 18);
  doc.setFont("Lib", "normal"); doc.setFontSize(10); doc.text("Φροντιστήριο «Φυσική Επιλογή» — Φροντιστήρια Μ.Ε.", 50, 25);
  doc.setFontSize(9); doc.setTextColor(255, 255, 255); doc.text(`${ev.subject} · ${ev.period}`, 50, 32);

  doc.setTextColor(0); doc.setFont("Lib", "bold"); doc.setFontSize(14); doc.text(student.name, 14, 56);
  doc.setFont("Lib", "normal"); doc.setFontSize(10); doc.setTextColor(90);
  doc.text(`Τάξη: ${student.grade || "—"}`, 14, 62);
  doc.text(`Καθηγητής/τρια: ${ev.teacher_name || "Φυσική Επιλογή"}`, 14, 67);
  doc.text(`Ημερομηνία: ${fmtD(ev.created_at)}`, 14, 72);

  doc.setFillColor(245, 246, 250); doc.roundedRect(140, 50, 56, 26, 2, 2, "F");
  doc.setFont("Lib", "normal"); doc.setFontSize(8); doc.setTextColor(110); doc.text("ΓΕΝΙΚΗ ΑΞΙΟΛΟΓΗΣΗ", 168, 56, { align: "center" });
  doc.setFont("Lib", "bold"); doc.setFontSize(24); doc.setTextColor(...color(ev.overall)); doc.text(`${ev.overall}`, 160, 68, { align: "center" });
  doc.setFontSize(10); doc.setTextColor(110); doc.text("/20", 168, 68);
  doc.setFontSize(8); doc.setTextColor(...color(ev.overall)); doc.text(verdict(ev.overall), 168, 73.5, { align: "center" });

  autoTable(doc, {
    startY: 82,
    head: [["Κριτήριο", "Βαθμός", "Χαρακτηρισμός"]],
    body: EVAL_CRITERIA.map(([k, l]) => [l, `${ev[k]} / 20`, verdict(ev[k])]),
    styles: { font: "Lib", fontSize: 10, cellPadding: 3.5 },
    headStyles: { fillColor: NAVY, textColor: 255, font: "Lib", fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 246, 250] },
    columnStyles: { 0: { cellWidth: 90 }, 1: { cellWidth: 35, halign: "center", fontStyle: "bold" }, 2: { cellWidth: 57 } },
    didParseCell: (d) => { if (d.section === "body" && d.column.index === 1) d.cell.styles.textColor = color(ev[EVAL_CRITERIA[d.row.index][0]]); },
  });

  let y = doc.lastAutoTable.finalY + 12;
  if (ev.comment) {
    doc.setFont("Lib", "bold"); doc.setFontSize(11); doc.setTextColor(...NAVY); doc.text("Σχόλιο καθηγητή", 14, y);
    doc.setDrawColor(...NAVY); doc.setLineWidth(0.8); doc.line(14, y + 3, 14, y + 3 + 6 * Math.ceil(doc.splitTextToSize(ev.comment, 170).length));
    doc.setFont("Lib", "normal"); doc.setFontSize(10); doc.setTextColor(40);
    const lines = doc.splitTextToSize(ev.comment, 170);
    doc.text(lines, 18, y + 7);
    y += 12 + lines.length * 5.5;
  }

  doc.setDrawColor(220); doc.setLineWidth(0.3); doc.line(14, 278, W - 14, 278);
  doc.setFont("Lib", "normal"); doc.setFontSize(8); doc.setTextColor(120);
  doc.text("Φροντιστήριο «Φυσική Επιλογή» · 25ης Μαρτίου 64, Πετρούπολη · 210 50 65 275 · fysikiepilogi.gr", 14, 284);
  doc.text("Η αξιολόγηση αποτυπώνει την εικόνα του μαθητή για τη συγκεκριμένη περίοδο και προορίζεται για τους γονείς/κηδεμόνες.", 14, 289);

  doc.save(`Αξιολόγηση_${student.name.replace(/\s+/g, "_")}_${ev.subject}_${ev.period.replace(/\s+/g, "_")}.pdf`);
}
